import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LinkContainer } from 'react-router-bootstrap'
import { Row, Col, Button, Spinner } from 'react-bootstrap'
import axios from '../axios'
import { getHomeHero } from '../actions/homeActions'

// ── Reuse the exact same HeroCarousel machinery from HomeScreen ────────────────

// ── Hero carousel helpers ──────────────────────────────────────────────────────

function buildSlides(heroData) {
  if (!heroData) return []
  const { tournament, contests, awards } = heroData
  // Tournament logo + name appear top-right on EVERY slide as branding
  const branding = { logoUrl: tournament.logo_url, tournamentName: tournament.name, date: tournament.start_date }
  const slides = []

  if (tournament.winners && tournament.winners.length) {
    slides.push({
      type: 'tournament', tag: 'BNB Champions', title: tournament.name,
      winners: tournament.winners, prizeMoney: null,
      imageUrl: tournament.image_url,
      ...branding,
    })
  }
  contests.forEach(c => {
    if (c.winner) slides.push({
      type: 'contest', tag: '🎯 Contest', title: c.name,
      winners: [c.winner], prizeMoney: c.prize_money || null,
      imageUrl: c.image_url || tournament.image_url,
      resultYards: c.result_yards || null,
      ...branding,
    })
  })
  awards.forEach(a => {
    if (a.winner) slides.push({
      type: 'award', tag: '🎖️ Award', title: a.name,
      winners: [a.winner], prizeMoney: null,
      description: a.description || '',
      imageUrl: a.image_url || tournament.image_url,
      ...branding,
    })
  })
  return slides
}

// WinnerCard — auto-width (sized to image), no overlays, no shadows
function WinnerCard({ winner, index = 0 }) {
  return (
    <div style={{
      position: 'relative', height: '100%', flexShrink: 0,
      marginLeft: index > 0 ? '-38px' : 0,
      zIndex: index,
    }}>
      {winner.image
        ? <img src={winner.image} alt={winner.name}
            style={{ height: '100%', width: 'auto', display: 'block' }} />
        : /* Default profile image */
          <img src="/bnb-profile-default.png" alt="Player"
            style={{ height: '100%', width: 'auto', display: 'block' }} />
      }
      {/* Name pill */}
      <div style={{
        position: 'absolute', bottom: '0.5rem', left: 0, right: 0,
        display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          background: 'var(--bnb-navy)',
          border: '1.5px solid var(--bnb-gold)',
          borderRadius: 999,
          padding: '0.2rem 0.7rem',
          color: '#fff', fontWeight: 900, fontStyle: 'italic',
          fontSize: 'clamp(0.6rem, 1.4vw, 0.82rem)',
          whiteSpace: 'nowrap', maxWidth: '90%',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {winner.name}
        </div>
      </div>
    </div>
  )
}

function DefaultHero({ userInfo }) {
  return (
    <div className="bnb-hero mb-4">
      <div className="bnb-hero-eyebrow"><i className="fa-solid fa-flag me-2"></i>Official Club Site</div>
      <h1 className="bnb-hero-title">
        {userInfo ? `Welcome back, ${userInfo.name.split(' ')[0]}.` : 'BNB Golf'}
      </h1>
      <p className="bnb-hero-sub">
        {userInfo
          ? 'Track your stats, register for tournaments, and compete for the championship.'
          : 'The official home of BNB Golf Club — tournaments, stats, and more.'}
      </p>
      {!userInfo && (
        <LinkContainer to="/login">
          <Button variant="outline-light" className="mt-3"
            style={{ fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.8rem' }}>
            Sign In
          </Button>
        </LinkContainer>
      )}
    </div>
  )
}

// Per-slide background overlay palette — cycles for contests/awards
const SLIDE_OVERLAYS = [
  ['rgba(0,22,72,0.68)',  'rgba(0,8,32,0.82)'],     // 0: deep navy   (tournament)
  ['rgba(0,64,76,0.68)',  'rgba(0,20,36,0.82)'],     // 1: teal
  ['rgba(52,14,84,0.68)', 'rgba(16,4,34,0.82)'],     // 2: purple
  ['rgba(4,60,36,0.68)',  'rgba(0,18,10,0.82)'],     // 3: forest
  ['rgba(78,34,0,0.68)',  'rgba(30,10,0,0.82)'],     // 4: amber
  ['rgba(62,4,18,0.68)',  'rgba(22,0,6,0.82)'],      // 5: crimson
  ['rgba(18,28,64,0.68)', 'rgba(6,8,30,0.82)'],      // 6: indigo
]

function HeroCarousel({ heroData, userInfo }) {
  const slides = useMemo(() => buildSlides(heroData), [heroData])
  const [current, setCurrent] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => { setCurrent(0) }, [heroData])

  useEffect(() => {
    if (paused || slides.length <= 1) return
    const t = setInterval(() => setCurrent(c => (c + 1) % slides.length), 5500)
    return () => clearInterval(t)
  }, [paused, slides.length])

  if (!slides.length) return <DefaultHero userInfo={userInfo} />

  return (
    <>
    <div
      style={{ borderRadius: 18, overflow: 'hidden', height: 420, position: 'relative', marginBottom: 0, cursor: 'default', userSelect: 'none' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, i) => {
        const [c1, c2] = SLIDE_OVERLAYS[i % SLIDE_OVERLAYS.length]
        const bg = slide.imageUrl
          ? `linear-gradient(135deg, ${c1} 0%, ${c2} 100%), url(${slide.imageUrl}) center/cover no-repeat`
          : `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`
        const isActive = i === current

        return (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            background: bg,
            opacity: isActive ? 1 : 0,
            transform: isActive ? 'scale(1)' : 'scale(1.035)',
            transition: 'opacity 0.75s ease, transform 0.75s ease',
            pointerEvents: isActive ? 'auto' : 'none',
          }}>
            {/* Sparkles */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
              {['✦','✧','✦','✧','✦','✧'].map((s, j) => (
                <span key={j} style={{
                  position: 'absolute',
                  top: `${12 + j * 14}%`,
                  left: `${8 + j * 15}%`,
                  color: 'var(--bnb-gold)',
                  fontSize: j % 2 === 0 ? '0.7rem' : '0.45rem',
                  opacity: 0.55,
                  animation: `heroSparkle ${2.2 + j * 0.35}s ease-in-out infinite`,
                  animationDelay: `${j * 0.4}s`,
                }}>{s}</span>
              ))}
            </div>

            {/* Content layer */}
            <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', padding: '1.5rem 2rem 0' }}>

              {/* ── Top row ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
                {/* LEFT: tag + title + date */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.22rem', maxWidth: '55%' }}>
                  <div style={{ color: 'var(--bnb-gold)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.16em', fontWeight: 900, textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
                    {slide.tag}
                  </div>
                  <div style={{ color: '#fff', fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.2, textShadow: '0 2px 12px rgba(0,0,0,0.9)' }}>
                    {slide.title}
                  </div>
                  {slide.date && (
                    <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.62rem', textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
                      {new Date(slide.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </div>
                {/* RIGHT: tournament logo + name */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                  {slide.logoUrl
                    ? <img src={slide.logoUrl} alt="" style={{ width: 96, height: 96, objectFit: 'contain', filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.75))' }} />
                    : <span style={{ fontSize: '3rem', filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.8))' }}>⛳</span>
                  }
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.72rem', textAlign: 'center', maxWidth: 120, lineHeight: 1.25, textShadow: '0 1px 8px rgba(0,0,0,0.9)' }}>
                    {slide.tournamentName}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Congratulations: tournament only, centered at top row level ── */}
            {slide.type === 'tournament' && <div className={`bnb-tournament-congrats bnb-tournament-congrats-${slide.winners.length}`} style={{
              position: 'absolute', top: '1.5rem', left: 0, right: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
              pointerEvents: 'none', zIndex: 2,
            }}>
              <div style={{
                fontWeight: 900, fontSize: '1.5rem', letterSpacing: '0.18em', textTransform: 'uppercase',
                background: 'linear-gradient(90deg, #c8a951 0%, #fff 30%, #f0d080 55%, #c8a951 80%, #fff 100%)',
                backgroundSize: '250% auto',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                animation: 'shimmerText 2.8s linear infinite',
              }}>
                <span className="mobileHide">🏆</span> &nbsp;Congratulations&nbsp; <span className="mobileHide">🏆</span>
              </div>
              <div style={{
                color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.8rem',
                letterSpacing: '0.05em', textAlign: 'center',
                textShadow: '0 1px 8px rgba(0,0,0,0.9)',
              }}>
                for winning BNB <span style={{ color: 'var(--bnb-gold)', fontWeight: 900 }}>{slide.title}</span>
              </div>
              {slide.prizeMoney && (
                <div style={{
                  marginTop: '0.25rem',
                  background: 'rgba(200,169,81,0.14)',
                  border: '1px solid rgba(200,169,81,0.45)',
                  borderRadius: 12, padding: '0.3rem 1.2rem',
                  textAlign: 'center',
                  animation: 'confettiBounce 3s ease-in-out infinite',
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.52rem', textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                    Winner takes home
                  </div>
                  <div style={{
                    fontWeight: 900, fontSize: '1.5rem', lineHeight: 1.1,
                    background: 'linear-gradient(90deg, #c8a951, #f5e07a, #c8a951)',
                    backgroundSize: '200% auto',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                    animation: 'shimmerText 2.2s linear infinite',
                  }}>
                    {slide.prizeMoney}
                  </div>
                </div>
              )}
            </div>}

            {/* ── Body: type-specific layout ── */}

            {slide.type === 'tournament' && (
              <div className={`bnb-tournament-winners bnb-tournament-winners-${slide.winners.length}`} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', height: 315, justifyContent: 'center', gap: 0, overflow: 'hidden' }}>
                {slide.winners.map((w, j) => <WinnerCard key={j} winner={w} index={j} />)}
              </div>
            )}

            {slide.type === 'contest' && (
              <>
                {/* LEFT: congratulations + prize money */}
                <div className="bnb-contest-text" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: '2rem', width: '42%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ fontWeight: 900, fontSize: '1.1rem', letterSpacing: '0.18em', textTransform: 'uppercase', background: 'linear-gradient(90deg, #c8a951 0%, #fff 30%, #f0d080 55%, #c8a951 80%, #fff 100%)', backgroundSize: '250% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmerText 2.8s linear infinite', textAlign: 'center' }}>
                    <span className="bnb-contest-emoji-left">🏆 &nbsp;</span>
                    Congratulations
                    <span className="bnb-contest-emoji-right">&nbsp; 🏆</span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.05em', textAlign: 'center', textShadow: '0 1px 8px rgba(0,0,0,0.9)', marginBottom: '0.2rem' }}>
                    <span className="MVshow">For winning </span><span style={{ color: 'var(--bnb-gold)', fontWeight: 900 }}>{slide.title}</span>
                  </div>
                  {slide.resultYards && (
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em', textAlign: 'center', textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
                      {slide.resultYards} Distance
                    </div>
                  )}
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.52rem', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, marginTop: '0.3rem' }}>Contest Prize</div>
                  <div style={{ fontWeight: 900, fontSize: '2.8rem', lineHeight: 1.1, background: 'linear-gradient(90deg, #c8a951, #f5e07a, #c8a951)', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmerText 2.2s linear infinite' }}>
                    ${slide.prizeMoney || '—'}
                  </div>
                </div>
                {/* RIGHT: player image */}
                <div className="bnb-contest-player" style={{ position: 'absolute', bottom: 0, right: '22%', height: 300, pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ position: 'relative', height: '100%' }}>
                    <img src={slide.winners[0]?.image || '/bnb-profile-default.png'} alt={slide.winners[0]?.name} style={{ height: '100%', width: 'auto', display: 'block' }} />
                    <div style={{ position: 'absolute', bottom: '0.5rem', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <div style={{ background: 'var(--bnb-navy)', border: '1.5px solid var(--bnb-gold)', borderRadius: 999, padding: '0.2rem 0.7rem', color: '#fff', fontWeight: 900, fontStyle: 'italic', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {slide.winners[0]?.name}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {slide.type === 'award' && (
              <>
                {/* LEFT: player image — moved toward center */}
                <div className="bnb-award-player" style={{ position: 'absolute', bottom: 0, left: '22%', height: 300, pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ position: 'relative', height: '100%' }}>
                    <img src={slide.winners[0]?.image || '/bnb-profile-default.png'} alt={slide.winners[0]?.name} style={{ height: '100%', width: 'auto', display: 'block' }} />
                    <div style={{ position: 'absolute', bottom: '0.5rem', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                      <div style={{ background: 'var(--bnb-navy)', border: '1.5px solid var(--bnb-gold)', borderRadius: 999, padding: '0.2rem 0.7rem', color: '#fff', fontWeight: 900, fontStyle: 'italic', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {slide.winners[0]?.name}
                      </div>
                    </div>
                  </div>
                </div>
                {/* RIGHT: congratulations + award description */}
                <div className="bnb-award-text" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: '2rem', width: '42%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem', pointerEvents: 'none', zIndex: 2 }}>
                  <div style={{ fontWeight: 900, fontSize: '1.1rem', letterSpacing: '0.18em', textTransform: 'uppercase', textAlign: 'center', background: 'linear-gradient(90deg, #c8a951 0%, #fff 30%, #f0d080 55%, #c8a951 80%, #fff 100%)', backgroundSize: '250% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', animation: 'shimmerText 2.8s linear infinite' }}>
                    <span className="bnb-award-emoji-left">🏆 &nbsp;</span>
                    Congratulations
                    <span className="bnb-award-emoji-right">&nbsp; 🏆</span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.05em', textShadow: '0 1px 8px rgba(0,0,0,0.9)', marginBottom: '0.2rem', textAlign: 'center' }}>
                    <span className="MVshow">For winning </span><span style={{ color: 'var(--bnb-gold)', fontWeight: 900 }}>{slide.title}</span>
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.88rem', lineHeight: 1.55, fontWeight: 600, textShadow: '0 1px 8px rgba(0,0,0,0.9)', textAlign: 'center' }}>
                    {slide.description || slide.title}
                  </div>
                </div>
              </>
            )}
          </div>
        )
      })}

    </div>

    {/* ── Dot nav: below the card ── */}
    {slides.length > 1 && (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.45rem', marginTop: '0.6rem', marginBottom: '0.5rem' }}>
        {slides.map((_, k) => (
          <button
            key={k}
            onClick={() => setCurrent(k)}
            style={{
              width: k === current ? 22 : 7, height: 7, borderRadius: 4,
              background: k === current ? 'var(--bnb-gold)' : 'rgba(0,30,87,0.25)',
              border: k === current ? 'none' : '1px solid rgba(0,30,87,0.3)',
              cursor: 'pointer', padding: 0,
              transition: 'all 0.35s ease',
              boxShadow: k === current ? '0 0 8px rgba(200,169,81,0.5)' : 'none',
            }}
          />
        ))}
      </div>
    )}
    </>
  )
}

// ── Accolade pill ─────────────────────────────────────────────────────────────

const ACCOLADE_COLORS = {
    tournament: { bg: 'rgba(200,169,81,0.12)', border: 'rgba(200,169,81,0.55)', color: 'var(--bnb-gold)' },
    award:      { bg: 'rgba(0,48,135,0.08)',   border: 'rgba(0,48,135,0.3)',    color: 'var(--bnb-navy)' },
    contest:    { bg: 'rgba(74,222,128,0.1)',  border: 'rgba(74,222,128,0.4)',  color: '#16a34a' },
}

function AccoladePill({ accolade }) {
    const c = ACCOLADE_COLORS[accolade.type] || ACCOLADE_COLORS.award
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
            background: c.bg, border: `1px solid ${c.border}`,
            borderRadius: 999, padding: '0.12rem 0.55rem',
            fontSize: '0.62rem', fontWeight: 700, color: c.color,
            whiteSpace: 'nowrap',
        }}>
            {accolade.type === 'tournament' ? '🏆' : accolade.type === 'award' ? '🎖️' : '🎯'}
            {accolade.label}{accolade.year ? ` '${String(accolade.year).slice(2)}` : ''}
        </span>
    )
}

// ── Country flag helpers (matching LeaderboardScreen) ────────────────────────

const COUNTRY_CODES = {
    'united states': 'us', 'usa': 'us', 'u.s.a.': 'us', 'u.s.': 'us',
    'canada': 'ca', 'mexico': 'mx', 'united kingdom': 'gb', 'uk': 'gb', 'england': 'gb',
    'france': 'fr', 'germany': 'de', 'spain': 'es', 'italy': 'it',
    'brazil': 'br', 'argentina': 'ar', 'colombia': 'co', 'chile': 'cl',
    'australia': 'au', 'new zealand': 'nz', 'japan': 'jp', 'china': 'cn',
    'south korea': 'kr', 'korea': 'kr', 'india': 'in', 'nigeria': 'ng',
    'ghana': 'gh', 'south africa': 'za', 'kenya': 'ke', 'jamaica': 'jm',
    'trinidad': 'tt', 'trinidad and tobago': 'tt', 'bahamas': 'bs',
    'dominican republic': 'do', 'puerto rico': 'pr', 'cuba': 'cu',
    'haiti': 'ht', 'panama': 'pa', 'costa rica': 'cr',
}

function flagSvgUrl(country) {
    if (!country) return null
    const lower = country.trim().toLowerCase()
    const code = lower.length === 2 ? lower : (COUNTRY_CODES[lower] || null)
    return code ? `https://flagcdn.com/${code}.svg` : null
}

function FlagBadge({ country, size = 22 }) {
    const url = flagSvgUrl(country)
    if (!url) return null
    return (
        <div style={{
            position: 'absolute', bottom: -2, right: -3,
            width: size, height: size,
            borderRadius: '50%', overflow: 'hidden',
            background: '#e5e7eb',
            border: '1.5px solid rgba(255,255,255,0.95)',
            boxShadow: '0 1px 5px rgba(0,0,0,0.28)',
            zIndex: 2,
        }}>
            <img src={url} alt={country} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
    )
}

// ── Player card ───────────────────────────────────────────────────────────────

function PlayerCard({ player }) {
    return (
        <div style={{
            display: 'flex', gap: '1rem', alignItems: 'flex-start',
            background: '#fff', border: '1.5px solid var(--bnb-border)',
            borderRadius: 14, padding: '0.9rem 1rem',
            boxShadow: '0 1px 6px rgba(0,48,135,0.05)',
            transition: 'box-shadow 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,48,135,0.12)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,48,135,0.05)'}
        >
            {/* Avatar with country badge */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    border: '2.5px solid var(--bnb-navy)',
                    overflow: 'hidden', background: '#f1f5f9',
                }}>
                    <img
                        src={player.image || '/bnb-profile-default.png'}
                        alt={player.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                </div>
                <FlagBadge country={player.country} size={22} />
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 800, fontSize: '0.95rem', color: 'var(--bnb-navy)' }}>{player.name}</span>
                    {player.handicap && (
                        <span style={{ fontSize: '0.68rem', color: 'var(--bnb-gray)', fontWeight: 600 }}>HCP {player.handicap}</span>
                    )}
                </div>

                {/* Meta row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.45rem' }}>
                    {player.hometown && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--bnb-gray)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                            <i className="fa-solid fa-location-dot" style={{ fontSize: '0.65rem', color: 'var(--bnb-navy)' }}></i>
                            {player.hometown}
                        </span>
                    )}
                    {player.college && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--bnb-gray)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                            <i className="fa-solid fa-graduation-cap" style={{ fontSize: '0.65rem', color: 'var(--bnb-navy)' }}></i>
                            {player.college}
                        </span>
                    )}
                    {player.dominant_hand && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--bnb-gray)', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                            <i className="fa-solid fa-hand" style={{ fontSize: '0.65rem', color: 'var(--bnb-navy)' }}></i>
                            {player.dominant_hand}-handed
                        </span>
                    )}
                </div>

                {/* Accolades */}
                {player.accolades.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {player.accolades.map((a, i) => <AccoladePill key={i} accolade={a} />)}
                    </div>
                )}
            </div>
        </div>
    )
}

// ── Player list row (PGA-style) ───────────────────────────────────────────────

function PlayerRow({ player }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '1rem',
            padding: '0.85rem 0',
            borderBottom: '1px solid var(--bnb-border)',
            transition: 'background 0.12s',
            cursor: 'default',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,48,135,0.03)'}
        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
            {/* Avatar with country badge */}
            <div style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', background: '#f1f5f9', border: '2.5px solid var(--bnb-navy)' }}>
                    <img src={player.image || '/bnb-profile-default.png'} alt={player.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
                <FlagBadge country={player.country} size={26} />
            </div>

            {/* Name + hometown */}
            <div>
                <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#111827', lineHeight: 1.2 }}>{player.name}</div>
                {player.hometown && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--bnb-gray)', marginTop: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <i className="fa-solid fa-location-dot" style={{ fontSize: '0.62rem', color: 'var(--bnb-navy)' }}></i>
                        {player.hometown}
                    </div>
                )}
            </div>
        </div>
    )
}

// ── Main screen ───────────────────────────────────────────────────────────────

function PlayersScreen() {
    const [slides, setSlides]   = useState([])
    const [players, setPlayers] = useState([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch]   = useState('')
    const [viewMode, setViewMode] = useState('card') // 'card' | 'list'
    const userLogin = useSelector(state => state.userLogin)
    const { userInfo } = userLogin
    const { data: heroData } = useSelector(state => state.homeHero)
    useEffect(() => {
        Promise.all([
            axios.get('/api/players/hero/'),
            axios.get('/api/players/'),
        ]).then(([heroRes, playersRes]) => {
            setSlides(heroRes.data || [])
            setPlayers(playersRes.data || [])
            setLoading(false)
        }).catch(() => setLoading(false))
    }, [])

    const filtered = useMemo(() => {
        if (!search.trim()) return players
        const q = search.toLowerCase()
        return players.filter(p =>
            p.name.toLowerCase().includes(q) ||
            (p.hometown || '').toLowerCase().includes(q) ||
            (p.college || '').toLowerCase().includes(q) ||
            (p.country || '').toLowerCase().includes(q)
        )
    }, [players, search])

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Hero Carousel ─────────────────────────────────────── */}
            <HeroCarousel heroData={heroData} userInfo={userInfo} />

            {/* ── Section title ── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: '0.75rem',
                borderBottom: '2px solid var(--bnb-navy)',
                paddingBottom: '0.4rem', marginTop: '1.5rem', marginBottom: '1rem',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: 'var(--bnb-navy)', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <i className="fa-solid fa-users"></i> Players
                        {!loading && <span style={{ fontWeight: 400, color: 'var(--bnb-gray)' }}>({filtered.length})</span>}
                    </div>

                    {/* View toggle tabs */}
                    <div style={{ display: 'flex', gap: '0.15rem', background: '#f1f5f9', borderRadius: 8, padding: '0.2rem' }}>
                        {[{id:'card', icon:'fa-table-cells-large', label:'Cards'}, {id:'list', icon:'fa-list', label:'List'}].map(v => (
                            <button key={v.id} onClick={() => setViewMode(v.id)} style={{
                                display: 'flex', alignItems: 'center', gap: '0.3rem',
                                background: viewMode === v.id ? '#fff' : 'transparent',
                                border: 'none', borderRadius: 6,
                                padding: '0.25rem 0.65rem',
                                fontSize: '0.7rem', fontWeight: viewMode === v.id ? 700 : 500,
                                color: viewMode === v.id ? 'var(--bnb-navy)' : 'var(--bnb-gray)',
                                cursor: 'pointer',
                                boxShadow: viewMode === v.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                                transition: 'all 0.15s',
                            }}>
                                <i className={`fa-solid ${v.icon}`} style={{ fontSize: '0.68rem' }}></i>
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Search */}
                <div style={{ position: 'relative' }}>
                    <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--bnb-gray)', fontSize: '0.7rem', pointerEvents: 'none' }}></i>
                    <input
                        type="text"
                        placeholder="Search players…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{
                            paddingLeft: '2rem', paddingRight: '0.75rem', paddingTop: '0.38rem', paddingBottom: '0.38rem',
                            border: '1.5px solid var(--bnb-border)', borderRadius: 8,
                            fontSize: '0.8rem', outline: 'none', width: 200,
                            color: '#111827',
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--bnb-gray)' }}>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⛳</div>
                    Loading players…
                </div>
            ) : viewMode === 'card' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', paddingBottom: '3rem' }}>
                    {filtered.length ? filtered.map(p => <PlayerCard key={p.id} player={p} />) : (
                        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--bnb-gray)', fontSize: '0.9rem' }}>
                            No players found{search ? ` for "${search}"` : ''}.
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ paddingBottom: '3rem' }}>
                    {(() => {
                        if (!filtered.length) return (
                            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--bnb-gray)', fontSize: '0.9rem' }}>
                                No players found{search ? ` for "${search}"` : ''}.
                            </div>
                        )
                        // Group by first letter of last name
                        const groups = {}
                        filtered.forEach(p => {
                            const letter = (p.last_name || p.name || '?')[0].toUpperCase()
                            if (!groups[letter]) groups[letter] = []
                            groups[letter].push(p)
                        })
                        return Object.keys(groups).sort().map(letter => (
                            <div key={letter}>
                                {/* Letter header */}
                                <div style={{ paddingTop: '1.25rem', paddingBottom: '0.3rem' }}>
                                    <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#111827', lineHeight: 1 }}>{letter}</div>
                                    <div style={{ borderBottom: '2px solid #111827', marginTop: '0.25rem' }} />
                                </div>
                                {groups[letter].map(p => <PlayerRow key={p.id} player={p} />)}
                            </div>
                        ))
                    })()}
                </div>
            )}
        </div>
    )
}

export default PlayersScreen
